/* eslint-disable sort-keys */
import { useTranslate } from '@im/react-localization';
import {
  FormField,
  useVendorProgramTemplateFields,
} from '../VendorProgramTemplate/hook/program-template';
import upperFirst from 'lodash/upperFirst';
import { atom, useRecoilState, useSetRecoilState } from 'recoil';
import { useBuildVars } from 'src/config';
import { useApiDownload, useApiGet, useApiPost } from 'src/api';
import { useMutation } from 'react-query';
import {
  programNameRegex,
  vendorCodeRegex,
} from '../VendorProgramTemplate/hook/program-template-error-handler';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type MasterVendorFormFields = {
  businessType: string;
  companyCode: string;
  costBasis: string;
  discountType: string;
  masterVendorCode: string;
  source: string;
  vendorACOPDiscountType: string;
  vendorName: string;
};

export type MasterVendorFormKeys = keyof MasterVendorFormFields | 'vendorName';
export const useMasterVendorFormFields = (): Record<
  MasterVendorFormKeys,
  FormField
> => {
  const translate = useTranslate();
  const VendorProgramFormFields = useVendorProgramTemplateFields();
  const businessTypeOptions = useBusinessTypeOptions();
  return Object.entries({
    vendorName: {
      required: true,
    },
    masterVendorCode: {
      multiline: true,
      required: true,
    },
    companyCode: {
      disabled: true,
      required: true,
    },
    source: {
      required: true,
    },
    costBasis: VendorProgramFormFields.costBasis,
    discountType: {
      options: VendorProgramFormFields['discountType'].options,
      required: true,
    },
    vendorACOPDiscountType: {
      options: VendorProgramFormFields['vendorACOPDiscountType'].options,
      required: true,
    },
    businessType: {
      options: businessTypeOptions,
      required: true,
    },
  }).reduce(
    (acc, [field, attrs]) => ({
      ...acc,
      [field]: {
        ...attrs,
        name: translate(
          `VendorProgram.MasterVendorForm${upperFirst(field)}Name`
        ),
      },
    }),
    {} as Record<MasterVendorFormKeys, FormField>
  );
};

export type MasterVendorFormData = Partial<
  Record<MasterVendorFormKeys, string>
>;

const MasterVendorFormAtom = atom<MasterVendorFormData | undefined>({
  default: undefined,
  key: 'master-vendor',
});

const MasterVendorFormIsUpdatedAtom = atom<boolean>({
  default: false,
  key: 'master-vendor-updated',
});

export function useIsValidMasterForm(formData: MasterVendorFormData) {
  const translate = useTranslate();
  const errorArray: { field: string; message: string }[] = [];
  const validateFields: MasterVendorFormKeys[] = [
    'vendorName',
    'masterVendorCode',
  ];

  for (const field of validateFields) {
    if (
      field === 'vendorName' &&
      !programNameRegex.exec(formData[field] as string)
    ) {
      errorArray.push({
        field: field,
        message: translate('VendorProgram.AlertProgramDetailFormError'),
      });
    }
    if (
      field === 'masterVendorCode' &&
      !vendorCodeRegex.exec(formData[field] as string)
    ) {
      errorArray.push({
        field: field,
        message: translate('VendorProgram.AlertVenorCodeError'),
      });
    }
  }
  return errorArray.length ? errorArray : true;
}

export function useMasterVendorForm() {
  const [fromData, _setFormData] = useRecoilState(MasterVendorFormAtom);
  const setIsFormUpdated = useSetRecoilState(MasterVendorFormIsUpdatedAtom);

  const setFormData = (formData: Parameters<typeof _setFormData>[0]) => {
    setIsFormUpdated(Boolean(formData));
    _setFormData(formData);
  };
  return [fromData ?? {}, setFormData] as const;
}

export type CreateMasterVendorRequest = {
  businessType: string;
  companyCode: string;
  costBasis: string;
  createdBy: string;
  discountType: string;
  masterVendorCode: string;
  source: string;
  vendorACOPDiscountType: string;
  vendorName: string;
};

export type CreateMasterVendorResponse = {
  message: string;
  statusCode: number;
  translationKey: string;
  vendorID: number;
};

export function usePostCreateMasterVendor(request: CreateMasterVendorRequest) {
  const buildvars = useBuildVars();
  const apiPost = useApiPost({ host: buildvars.VP_API_BASE });
  return useMutation(() =>
    apiPost(
      '/system.webapi.gds/api/v1/gdsCoreApiVendor/CreateMasterVendorProgram',
      request
    )
  );
}

export function useGetMasterVendorList() {
  const buildvars = useBuildVars();
  const apiGetDownload = useApiDownload({ host: buildvars.VP_API_BASE });
  return useMutation(() =>
    apiGetDownload(
      '/system.webapi.gds/api/v1/gdsCoreApiVendor/MasterVendorProgram.xlsx'
    )
  );
}

function useBusinessTypeOptions() {
  const [options, setOptions] = useState<{ label: string; value: string }[]>(
    []
  );
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode');
  const companyCode = searchParams.get('companyCode');

  const buildVars = useBuildVars();
  const apiGet = useApiGet({ host: buildVars.VP_API_BASE });

  useEffect(
    () => {
      const fetchBusinessTypes = async () => {
        try {
          const response = await apiGet(
            '/system.webapi.gds/api/v1/gdsCoreApiVendor/GetBusinessTypes',
            {
              headers: {
                countryCode: countryCode || '',
                companyCode: companyCode || '',
              },
            }
          );

          if (Array.isArray(response)) {
            setOptions(
              response.map((item: string) => ({
                label: item,
                value: item,
              }))
            );
          }
        } catch (error) {
          setOptions([]);
        }
      };

      fetchBusinessTypes();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return options;
}
